/**
 * The plan an account is on, when it ends, and its credit balances. Read
 * only: plans and credits are set by an admin (screens/admin/Billing.tsx)
 * because nothing sells them yet.
 *
 * PAY AS YOU GO (2026-10-13). Compute past the monthly allowance is priced at
 * the plan's rate and paid from credits: as it builds up, hourly, where the
 * server has that on (`overage.paidAsUsed`, apps/cloud/src/services/billing/
 * accrual.ts), otherwise when the cycle closes (statements.ts). The row here
 * is the cycle's charge so far.
 *
 * CREDITS USED THIS CYCLE (2026-10-25) is its own component
 * (CreditsSpentBar.tsx), also shown on the projects list. When credits would
 * not cover the overage the screen says so and nothing else happens: nothing
 * is limited (warn only). The compute unit total is still never printed, only
 * dollars and the rate.
 *
 * SUBSCRIPTIONS (2026-10-13). Every plan is paid, starting with a one-cycle
 * Basic trial, and credits pay the plan at each cycle start unless the owner
 * turned that off (Account screen). The plan row says which of trial, renews,
 * ends or ended applies, and at most one notice says what happens next. A
 * shutdown is only promised when `billing.lapseEnforced` is true.
 *
 * Shown on the Usage screen's account scope and on the Account screen, from
 * the same account usage payload, so the two cannot disagree.
 */
import { useEffect } from 'react';

import { CreditsSpentBar } from './CreditsSpentBar';
import { dateLabel } from './DeployRow';
import { Notice } from './Notice';

import { captureEvent } from '../../lib/analytics/posthog';
import { t } from '../../lib/i18n/t';
import type { Billing, Plan, Usage } from '../../lib/types';

import '../../styles/components/usage.css';

/** US dollars, the currency credits are held in. The symbol is the same in both languages. */
function dollars(amount: string): string {
  return `$${amount}`;
}

const PLAN_NAMES: Record<Plan, string> = { basic: 'Basic', pro: 'Pro', max: 'Max' };

/** The plan row: which plan, and whether it is a trial, renews, ends or ended. */
function planReading(usage: Usage, billing: Billing | undefined): string {
  const plan = t(billing?.planLabel ?? usage.planLabel);
  const end = billing?.planEndsAt ?? usage.planExpiresAt;
  if (!billing || !end) {
    return usage.planExpiresAt
      ? t('{plan}, until {date}', { plan, date: dateLabel(usage.planExpiresAt) })
      : plan;
  }
  const date = dateLabel(end);
  if (billing.standing === 'lapsed') return t('{plan}, ended {date}', { plan, date });
  if (billing.standing === 'trial') return t('{plan} trial, until {date}', { plan, date });
  return billing.creditsPayPlan
    ? t('{plan}, renews {date}', { plan, date })
    : t('{plan}, ends {date}', { plan, date });
}

type NoticeKind = 'lapsed' | 'opted_out' | 'renewal_short' | null;

function noticeKind(billing: Billing): NoticeKind {
  if (billing.standing === 'lapsed') return 'lapsed';
  if (!billing.creditsPayPlan) return 'opted_out';
  if (!billing.renewalCovered && billing.renewalUsd) return 'renewal_short';
  return null;
}

/** At most one notice about what happens when the plan's cycle ends. */
function PlanNotice({ billing }: { billing: Billing }) {
  const date = billing.planEndsAt ? dateLabel(billing.planEndsAt) : '';
  const shutdown = billing.lapseEnforced
    ? ` ${t('Your apps will be shut down then, and their data deleted 30 days later.')}`
    : '';
  if (billing.standing === 'lapsed') {
    return (
      <Notice tone="attention" title={t('Your plan has ended')}>
        {billing.lapseEnforced
          ? t('It ended on {date}. Your apps were shut down, and their data is deleted 30 days after that. Ask the SV Cloud team to start a plan again.', { date })
          : t('It ended on {date}. Ask the SV Cloud team to start it again.', { date })}
      </Notice>
    );
  }
  if (!billing.creditsPayPlan) {
    return (
      <Notice tone="attention" title={t('Your plan will not renew')}>
        {t('Credits are not paying for your plan, so it ends on {date}.', { date })}
        {shutdown}
      </Notice>
    );
  }
  if (!billing.renewalCovered && billing.renewalUsd) {
    return (
      <Notice tone="attention" title={t('Your credits do not cover the next renewal')}>
        {t('Renewing on {date} costs {amount}.', { date, amount: dollars(billing.renewalUsd) })}
        {shutdown}
      </Notice>
    );
  }
  return null;
}

export function PlanSummary({ usage }: { usage: Usage }) {
  const credits = usage.credits;
  const promo = credits && Number(credits.promotionalUsd) > 0;
  const billing = usage.billing;
  const overage = usage.overage && usage.overage.units > 0 ? usage.overage : null;
  const uncovered = overage && Number(overage.uncoveredUsd) > 0 ? overage.uncoveredUsd : null;

  const notice = billing ? noticeKind(billing) : null;
  useEffect(() => {
    if (notice && billing) {
      captureEvent('plan_notice_shown', {
        kind: notice,
        plan: usage.plan,
        standing: billing.standing,
        lapse_enforced: billing.lapseEnforced,
      });
    }
  }, [notice]);

  useEffect(() => {
    if (overage) {
      captureEvent('usage_overage_shown', {
        plan: usage.plan,
        estimated_usd: Number(overage.estimatedUsd),
        charged_usd: Number(overage.chargedUsd),
        uncovered_usd: Number(overage.uncoveredUsd),
      });
    }
    // Once per estimate, not on every render.
  }, [overage?.estimatedUsd, overage?.uncoveredUsd]);

  return (
    <div className="plan-sum">
      <dl className="plan-sum__rows">
        <div className="plan-sum__row">
          <dt>{t('Plan')}</dt>
          <dd>
            {planReading(usage, billing)}
            {billing?.pendingPlanId && billing.planEndsAt ? (
              <span className="plan-sum__aside">
                {t('moves to {plan} on {date}', {
                  plan: t(PLAN_NAMES[billing.pendingPlanId]),
                  date: dateLabel(billing.planEndsAt),
                })}
              </span>
            ) : null}
          </dd>
        </div>
        {credits ? (
          <>
            <div className="plan-sum__row">
              <dt>{t('Credits')}</dt>
              <dd className="mono">{dollars(credits.paidUsd)}</dd>
            </div>
            <div className="plan-sum__row">
              <dt>{t('Promotional credits')}</dt>
              <dd>
                <span className="mono">{dollars(credits.promotionalUsd)}</span>
                {promo && credits.promotionalNextExpiry ? (
                  <span className="plan-sum__aside">
                    {t('first ones expire {date}', { date: dateLabel(credits.promotionalNextExpiry) })}
                  </span>
                ) : null}
              </dd>
            </div>
          </>
        ) : null}
        {overage ? (
          <div className="plan-sum__row">
            <dt>{t('Past your allowance')}</dt>
            <dd>
              <span className="mono">{t('{amount} so far', { amount: dollars(overage.estimatedUsd) })}</span>
              {Number(overage.chargedUsd) > 0 ? (
                <span className="plan-sum__aside">
                  {t('{amount} paid from credits', { amount: dollars(overage.chargedUsd) })}
                </span>
              ) : null}
              {overage.rateUsdPerMillion ? (
                <span className="plan-sum__aside">
                  {t('at {rate} per million compute units', { rate: dollars(overage.rateUsdPerMillion) })}
                </span>
              ) : null}
            </dd>
          </div>
        ) : null}
      </dl>
      {usage.creditsSpent ? <CreditsSpentBar spent={usage.creditsSpent} /> : null}
      {billing ? <PlanNotice billing={billing} /> : null}
      {uncovered ? (
        <Notice tone="attention" title={t('Your credits will not cover all of this')}>
          {t(
            '{amount} of it is not covered by your credits. Your apps keep running and nothing is limited. Ask the SV Cloud team to add credits.',
            { amount: dollars(uncovered) },
          )}
        </Notice>
      ) : null}
      {credits ? (
        <p className="plan-sum__note">
          {usage.overage?.paidAsUsed
            ? t(
                'Compute past your allowance is paid from your credits as you use it, promotional credits first. Storage past the allowance is not charged yet.',
              )
            : t(
                'Compute past your allowance is paid from your credits when each cycle ends, promotional credits first. Storage past the allowance is not charged yet.',
              )}
        </p>
      ) : null}
    </div>
  );
}
